import React, { useState } from "react";
import { Link } from "react-router-dom";
import fusshnLogo from "../../Assets/fusshn_logo.svg";
import useAuth from "../../hooks/use_auth";
import ProfileDropdown from "./components/ProfileDropDown";
import SelectCity from "../../common_components/selectcity";
import SearchComponent from "./components/SearchComponent";
import useUserLocation from "../../hooks/useUserLocation";

const cityMap = {
  "Delhi NCR": "DEL",
  "Delhi/NCR": "DEL",
  "Delhi": "DEL",
  "Mumbai": "MUM",
  "Bengaluru": "BLR",
  "Bangalore": "BLR",
  "Noida": "NOI",
  "Gurugram": "GGN",
  "Gurgaon": "GGN",
  "Jaipur": "JAI",
  "Indore": "IND",
  "Pune": "PUN",
  "Hyderabad": "HYD",
  "Chandigarh": "CHD",
  "Chennai": "CHE",
  "Ahmedabad": "AMD",
  "Kolkata": "KOL",
  "Lucknow": "LCK",
  "Surat": "SRT",
  "Nagpur": "NAG",
  "Bhopal": "BHO",
  "Vadodara": "VAD",
  "Kanpur": "KAN",
  "Ludhiana": "LDH",
  "Nashik": "NSK",
  "Patna": "PAT",
  "Agra": "AGR",
  "Ranchi": "RAN",
  "Rajkot": "RAJ",
  "Guwahati": "GUW",
  "Coimbatore": "COI",
  "Kochi": "KOC",
  "Thiruvananthapuram": "TVM",
  "Visakhapatnam": "VIZ",
  "Vijayawada": "VIJ",
  // Additional cities from comprehensive database
  "Allahabad": "ALD",
  "Amritsar": "ATR",
  "Aurangabad": "AUR",
  "Bareilly": "BRL",
  "Bhubaneswar": "BBS",
  "Cuttack": "CTC",
  "Dehradun": "DDN",
  "Dhanbad": "DNB",
  "Durgapur": "DGP",
  "Faridabad": "FBD",
  "Ghaziabad": "GZB",
  "Gorakhpur": "GKP",
  "Gwalior": "GWL",
  "Hubli": "HBX",
  "Jalandhar": "JLR",
  "Jammu": "JAM",
  "Jamshedpur": "JSR",
  "Jodhpur": "JDH",
  "Kota": "KTA",
  "Kozhikode": "CCJ",
  "Madurai": "MDU",
  "Mangalore": "MNG",
  "Meerut": "MRT",
  "Mysore": "MYS",
  "Pondicherry": "PND",
  "Raipur": "RPR",
  "Rourkela": "ROU",
  "Salem": "SLM",
  "Siliguri": "SLG",
  "Srinagar": "SXR",
  "Thane": "THN",
  "Thrissur": "TCR",
  "Tiruchirappalli": "TRY",
  "Tirunelveli": "TEN",
  "Tirupati": "TTP",
  "Udaipur": "UDR",
  "Ujjain": "UJN",
  "Varanasi": "VNS",
  "Vellore": "VLR",
  "Warangal": "WGL"
};

const Navbar = ({ username, hideBorder = false }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userLocation = useUserLocation();
  const selectedCity = userLocation?.city || "Select City";
  // First-visit gate: no stored location → the picker stays open and can't be
  // dismissed until a location is saved (mirrors mobile's first-location screen).
  const mustPickLocation = !userLocation;
  const user = useAuth();

  // Scroll awareness: gain blur + shadow once the page is scrolled
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setShowSearch(true);
      }
      if (event.key === 'Escape') {
        setShowSearch(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <nav className={`${scrolled ? 'bg-black/85 backdrop-blur-xl shadow-lg shadow-black/40 border-b border-gray-800/50' : `bg-black backdrop-blur-md ${hideBorder ? '' : 'border-b border-gray-800/50'}`} text-white fixed w-full top-0 left-0 z-50 px-4 sm:px-6 lg:px-[60px] py-4 box-border transition-all duration-300`}>
      <div className="flex items-center justify-between max-w-[1440px] mx-auto relative">
        {/* Left Section - Logo and City */}
        <div className="flex items-center gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-1 sm:gap-2">
            <img src={fusshnLogo} alt="Fusshn Logo" className="h-[32px] sm:h-[42px] w-auto" />
            <span 
              className="text-white font-poppins text-[16px] sm:text-[20.591px] font-normal leading-normal tracking-[0.206px] uppercase hidden xs:block"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              FUSSHN
            </span>
          </Link>
          <div className="relative hidden sm:block">
            <button 
              onClick={() => setModalOpen(true)} 
              className="flex items-center gap-2 bg-transparent border-none text-white font-normal cursor-pointer px-3 py-1.5 rounded-lg transition-colors duration-300 hover:bg-white/10 capitalize tracking-[0.14px]"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px' }}
            >
              {selectedCity.length <= 5 ? selectedCity : (cityMap[selectedCity] || selectedCity)}
              <svg className="w-[12px] h-[8px] flex-shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Center Section - Search Bar (Desktop Only) */}
        <div className="hidden lg:flex flex-1 justify-center max-w-md mx-8">
          {showSearch ? (
            <SearchComponent onClose={() => setShowSearch(false)} />
          ) : (
            <div 
              className="flex w-[420px] h-[42px] px-[18.67px] py-[11.2px] items-center gap-3 rounded-xl bg-[#202020] border border-transparent hover:border-[#17BD3B]/50 transition-all duration-300 focus-within:border-[#17BD3B] focus-within:shadow-lg focus-within:shadow-[#17BD3B]/20 cursor-pointer"
              onClick={() => setShowSearch(true)}
            >
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="flex-1 text-gray-400 text-sm font-medium">
                Search for Events, Workshop
              </span>
            </div>
          )}
        </div>

        {/* Mobile Search Button */}
        <button 
          className="lg:hidden bg-transparent border-none text-white cursor-pointer p-2 rounded-lg hover:bg-white/10 transition-colors duration-300" 
          onClick={() => setShowSearch(true)}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>

        {/* Right Section - Navigation and Auth (Desktop) */}
        <div className="hidden lg:flex items-center gap-4">
          <div className="flex gap-2">
            <Link to="/events-filters" className="no-underline">
              <button className="nav-underline bg-transparent border-none text-white text-sm font-medium cursor-pointer px-4 py-2.5 rounded-xl transition-all duration-300 hover:bg-white/10">
                Events
              </button>
            </Link>
            <Link to="/setup-account" className="no-underline">
              <button className="nav-underline bg-transparent border-none text-white text-sm font-medium cursor-pointer px-4 py-2.5 rounded-xl transition-all duration-300 hover:bg-white/10">
                List Your Event
              </button>
            </Link>
          </div>
          {user ? (
            <ProfileDropdown username={username} />
          ) : (
            <Link to="/login" className="no-underline">
              <button className="btn-shine bg-[#17BD3B] hover:bg-[#15a834] text-white border-none px-5 py-[7px] rounded-lg text-sm font-semibold cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#17BD3B]/25">
                Sign in
              </button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden bg-transparent border-none text-white cursor-pointer p-2 rounded-lg hover:bg-white/10 transition-colors duration-300" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-gray-900/95 backdrop-blur-md border border-gray-700/50 rounded-xl mt-2 mx-4 shadow-2xl overflow-hidden drop-in">
          {/* City Selector for Mobile */}
          <div className="p-4 border-b border-gray-700/50">
            <button 
              onClick={() => {
                setModalOpen(true);
                setMobileMenuOpen(false);
              }} 
              className="flex items-center justify-between w-full bg-gray-800/50 border border-gray-700/50 text-white font-medium cursor-pointer px-4 py-3 rounded-lg transition-colors duration-300 hover:bg-gray-700/50"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px' }}
            >
              <span className="capitalize">
                📍 {selectedCity.length <= 8 ? selectedCity : (cityMap[selectedCity] || selectedCity)}
              </span>
              <svg className="w-4 h-4 flex-shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Navigation Items */}
          <div className="p-4">
            <div className="flex flex-col gap-2">
              <Link to="/events-filters" className="no-underline" onClick={() => setMobileMenuOpen(false)}>
                <button className="bg-transparent border-none text-white text-sm font-medium cursor-pointer px-4 py-3 rounded-lg transition-colors duration-300 hover:bg-gray-800/50 text-left w-full">
                  Events
                </button>
              </Link>
              <Link to="/setup-account" className="no-underline" onClick={() => setMobileMenuOpen(false)}>
                <button className="bg-transparent border-none text-white text-sm font-medium cursor-pointer px-4 py-3 rounded-lg transition-colors duration-300 hover:bg-gray-800/50 text-left w-full">
                  List Your Event
                </button>
              </Link>
              
              {/* Auth Section */}
              <div className="mt-4 pt-4 border-t border-gray-700/50">
                {user ? (
                  <div className="w-full">
                    <ProfileDropdown username={username} />
                  </div>
                ) : (
                  <Link to="/login" className="no-underline" onClick={() => setMobileMenuOpen(false)}>
                    <button className="bg-gradient-to-r from-[#17BD3B] to-[#15a834] hover:from-[#15a834] hover:to-[#138a2e] text-white border-none px-4 py-3 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#17BD3B]/25 w-full text-center">
                      Sign in
                    </button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Search Overlay */}
      {showSearch && (
        <div className="lg:hidden fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex flex-col drop-in">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
            <h3 className="text-white text-lg font-medium">Search Events</h3>
            <button 
              onClick={() => setShowSearch(false)}
              className="bg-transparent border-none text-white cursor-pointer p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Search Content */}
          <div className="flex-1 p-4">
            <SearchComponent onClose={() => setShowSearch(false)} />
          </div>
        </div>
      )}

      <SelectCity
        isOpen={modalOpen || mustPickLocation}
        onClose={() => setModalOpen(false)}
        canClose={!mustPickLocation}
      />
    </nav>
  );
};

export default Navbar;
